import React, { useEffect, useState } from 'react';
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    CustomInput,
    Spinner,
    Alert,
    Row,
    Col,
    FormFeedback,
    ListGroup,
    ListGroupItem
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserById } from './redux/dispatcher';
import { fetchCompanyList } from "components/companyList/redux/dispatcher";
import { Chip } from '@mui/material';
import styled from '@emotion/styled';
import { List } from 'reactstrap'

const TextAreaDiv = styled('div')(({ theme }) => ({
    border: '1px solid #ced4da',
    borderRadius: '.25rem',
    padding: '.375rem .75rem',
    height: '100px',
    overflowY: 'auto',
    backgroundColor: '#e9ecef',
    cursor: 'not-allowed',
}));

function UpdateViewUser({ args, isViewOnly, data, setIsUpdate, setIsViewOnly, selectedRoleType,
    searchQuery }) {
    const dispatch = useDispatch();
    const userList = useSelector((state) => state.userListReducer);
    const companyListData =
        useSelector((state) => state.companyListReducer?.data?.data) || [];
    const [modal, setModal] = useState(true);
    const [status, setStatus] = useState(data?.status === 'active' ? true : false);
    const [firstName, setFirstName] = useState(data?.first_name);
    const [lastName, setLastName] = useState(data?.last_name);
    const [email, setEmail] = useState(data?.email_id);
    const [password, setPassword] = useState('');
    const [mobile, setMobile] = useState(data?.mobile_number);
    const [loading, setLoading] = useState(userList?.isLoading);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
    const [roleType, setRoleType] = useState(data?.role)
    const [confrimPassword, setConfrimPassword] = useState('')
    const [passwordUpdate, setPasswordUpdate] = useState(false)
    const [selectedCompanies, setSelectedCompanies] = useState(
        data?.company_id
            ? (Array.isArray(data?.company_id)
                ? data?.company_id.filter(id => id !== null && id !== undefined)
                : [])
            : []
    );
    const [companySearch, setCompanySearch] = useState('');
    const [formError, setFormError] = useState({
        firstName: false,
        lastName: false,
        email: false,
        mobile: false,
        password: false,
        confirmpassword: false,
        roleType: false
    })

    // Fetch company list when modal opens and role is fluid_handler
    useEffect(() => {
        if (modal && roleType === "fluid_handler") {
            dispatch(fetchCompanyList());
        }
    }, [dispatch, modal, roleType]);

    // Filter companies based on search
    const filteredCompanyList = companyListData.filter(company =>
        company?.company_name?.toLowerCase().includes(companySearch.toLowerCase())
    );

    // Check if all companies are selected
    const isAllCompaniesSelected = filteredCompanyList.length > 0 &&
        filteredCompanyList.every(company => selectedCompanies.includes(company?.id));

    // Toggle individual company
    const toggleCompany = (companyId) => {
        if (companyId === null || companyId === undefined) return;

        setSelectedCompanies(prev => {
            if (prev.includes(companyId)) {
                return prev.filter(id => id !== companyId);
            } else {
                return [...prev, companyId];
            }
        });
    };

    // Toggle select all companies
    const toggleSelectAllCompanies = () => {
        if (isAllCompaniesSelected) {
            // Remove all filtered companies from selection
            const filteredIds = filteredCompanyList.map(c => c?.id).filter(id => id !== null && id !== undefined);
            setSelectedCompanies(prev => prev.filter(id => !filteredIds.includes(id)));
        } else {
            // Add all filtered companies to selection
            const filteredIds = filteredCompanyList.map(c => c?.id).filter(id => id !== null && id !== undefined);
            setSelectedCompanies(prev => {
                const newIds = filteredIds.filter(id => !prev.includes(id));
                return [...prev, ...newIds];
            });
        }
    };

    const toggleModal = () => {
        setModal(!modal);
        setLoading(false);
        setFirstName('');
        setLastName('')
        setEmail('')
        setMobile('')
        setRoleType('')
        setPassword('')
        setConfrimPassword('')
        setSelectedCompanies([]);
        setCompanySearch('');
        setErrorMessage('');
        setIsUpdate(false);
        setIsViewOnly(false);
        setFormError({
            firstName: false,
            lastName: false,
            email: false,
            mobile: false,
            password: false,
            confirmpassword: false,
            roleType: false
        })
    };

    const togglePasswordVisibility = () => {
        setIsPasswordVisible(!isPasswordVisible);
    };

    const toggleCinfirmPasswordVisibility = () => {
        setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
    }

    const handleStatusChange = () => setStatus(!status);


    const handleFormSubmit = async (e) => {
        e.preventDefault();
        // Initialize an error object to track any form errors
        const newFormError = {
            firstName: !firstName,
            lastName: !lastName,
            mobile: mobile ? !/^\d{10}$/.test(mobile) : false, // Check mobile only if it's provided
        };

        // Set form errors and display error message if any required fields are missing
        if (newFormError.firstName || newFormError.lastName || newFormError.mobile) {
            setFormError(newFormError);
            setErrorMessage('Please correct the errors in the form.');
            setLoading(false);
            return;
        }

        // Check if passwords match only if they are provided
        if (password || confrimPassword) {
            if (password !== confrimPassword) {
                setErrorMessage('Passwords do not match.');
                setLoading(false);
                return;
            }
        }
        setErrorMessage('');

        try {
            const userData = {
                UUID: data?.UUID,
                first_name: firstName,
                last_name: lastName,
                password: password ? password : undefined,
                status: status ? 'active' : 'inactive',
                ...(mobile && { mobile_number: mobile }),
                ...(roleType === "fluid_handler" &&
                    selectedCompanies.length > 0 && { company_id: selectedCompanies }),
            };

            dispatch(
                updateUserById(
                    userData,
                    toggleModal,
                    selectedRoleType === "rt" ? null : selectedRoleType,
                    searchQuery
                )
            );

        } catch (err) {
            setErrorMessage('Error adding user. Please try again.')
        } finally {
            setLoading(false);
        }
    };


    //Regex for validation 
    const validateFirstName = (e) => {
        const value = e.target.value;
        if (!value) {
            setFormError(prevState => ({ ...prevState, firstName: false }));
            return;
        }
        const regex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]{2,25}$/;
        const isValid = regex.test(value);

        setFormError(prevState => ({ ...prevState, firstName: !isValid }));
    };

    const validateLastName = (e) => {
        const value = e.target.value;
        if (!value) {
            setError(false);
            setFormError(prevState => ({ ...prevState, lastName: false }));
            return;
        }
        const regex = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]{2,25}$/;
        const isValid = regex.test(value);

        setFormError(prevState => ({ ...prevState, lastName: !isValid }));
    };

    const validateEmail = (e) => {
        const value = e.target.value;
        if (!value) {
            setError(false);
            setFormError(prevState => ({ ...prevState, email: false }));
            return;
        }
        const regex = /^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/;
        const isValid = regex.test(value);

        setFormError(prevState => ({ ...prevState, email: !isValid }));
    };

    const validateMobile = (e) => {
        const value = e.target.value;
        if (!value) {
            setError(false);
            setFormError(prevState => ({ ...prevState, mobile: false }));
            return;
        }
        const regex = /^[0-9]{10}$/;
        const isValid = regex.test(value);

        setFormError(prevState => ({ ...prevState, mobile: !isValid }));
    };

    const validatePassword = (e) => {
        const value = e.target.value;
        if (!value) {
            setError(false);
            setFormError(prevState => ({ ...prevState, password: false }));
            return;
        }
        const regex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
        const isValid = regex.test(value);

        setFormError(prevState => ({ ...prevState, password: !isValid }));
    };

    const validateConfirmPassword = (e) => {
        const value = e.target.value;
        if (!value) {
            setError(false);
            setFormError(prevState => ({ ...prevState, confirmpassword: false }));
            return;
        }
        const regex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{8,}$/;
        const isValid = regex.test(value);

        setFormError(prevState => ({ ...prevState, confirmpassword: !isValid }));
    };
    return (
        <div>
            <Button color="primary" onClick={toggleModal}>
                Add User
            </Button>

            <Modal isOpen={modal} toggle={toggleModal} {...args} modalClassName="custom-modal">
                <h1 style={{ textAlign: 'center', borderBottom: '4px solid #135674', color: '#135674' }} className=" m-3">
                    {
                        isViewOnly ? 'View User' : 'Update User'
                    }
                </h1>
                <ModalBody>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="firstName">First Name<span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        id="firstName"
                                        name="firstName"
                                        placeholder="Enter First Name"
                                        type="text"
                                        value={firstName}
                                        invalid={formError.firstName}
                                        onChange={(e) => {
                                            setFirstName(e.target.value)
                                            validateFirstName(e)
                                        }}
                                        disabled={isViewOnly}
                                        maxLength={25}
                                    />
                                    <FormFeedback>First Name must only contain alphabets.</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="lastName">Last Name<span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        id="lastName"
                                        name="lastName"
                                        placeholder="Enter Last Name"
                                        type="text"
                                        value={lastName}
                                        invalid={formError.lastName}
                                        onChange={(e) => {
                                            setLastName(e.target.value)
                                            validateLastName(e)
                                        }}
                                        disabled={isViewOnly}
                                        maxLength={25}
                                    />
                                    <FormFeedback>Last Name must only contain alphabets.</FormFeedback>
                                </FormGroup>

                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="email">Email<span style={{ color: 'red' }}>*</span> (User ID)</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        placeholder="Enter email id"
                                        type="email"
                                        value={email}
                                        invalid={formError.email}
                                        onChange={(e) => {
                                            setEmail(e.target.value)
                                            validateEmail(e)
                                        }}
                                        disabled
                                    />
                                    <FormFeedback>Email must be a valid email address.</FormFeedback>
                                </FormGroup>

                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="mobile">Mobile</Label>
                                    <Input
                                        id="mobile"
                                        name="mobile"
                                        placeholder="Enter mobile"
                                        type="text"
                                        value={mobile}
                                        invalid={formError.mobile}
                                        onChange={(e) => {
                                            setMobile(e.target.value)
                                            validateMobile(e)
                                        }}
                                        disabled={isViewOnly}
                                        maxLength={10}
                                    />
                                    <FormFeedback>Mobile Number must be exactly 10 digits and contain only numbers.</FormFeedback>

                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="roletype">Select Role<span style={{ color: 'red' }}>*</span></Label>
                                    <Input
                                        type="select"
                                        name="selectRole"
                                        id="roletype"
                                        value={roleType}
                                        onChange={(e) => setRoleType(e.target.value)}
                                        disabled
                                    >
                                        <option value="">Select Role</option>
                                        <option value="company_admin">Company Admin</option>
                                        <option value="enterprise_admin">Enterprise Admin</option>
                                        <option value="fluid_handler">Fluid Handler</option>
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>

                        {roleType === "fluid_handler" && !isViewOnly && (
                            <Row>
                                <Col md={12}>
                                    <FormGroup className="mb-2">
                                        <Label className="mb-1">Companies (Optional)</Label>
                                        <div style={{ border: '1px solid #ced4da', borderRadius: 4, padding: 6, background: '#fff', maxHeight: 300, overflowY: 'auto' }}>
                                            <div className="mb-2">
                                                <Input
                                                    placeholder="Search Company"
                                                    value={companySearch}
                                                    onChange={e => setCompanySearch(e.target.value)}
                                                    style={{ padding: '4px 8px', fontSize: '14px' }}
                                                />
                                            </div>
                                            <div className="mb-2">
                                                <div className="custom-control custom-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        className="custom-control-input"
                                                        id="selectAllCompanies"
                                                        checked={isAllCompaniesSelected}
                                                        onChange={toggleSelectAllCompanies}
                                                    />
                                                    <label
                                                        className="custom-control-label"
                                                        htmlFor="selectAllCompanies"
                                                        style={{ fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {isAllCompaniesSelected ? 'Clear All' : 'Select All'}
                                                    </label>
                                                </div>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                                                {filteredCompanyList.map(company => {
                                                    const id = company?.id;
                                                    return (
                                                        <div key={id} className="custom-control custom-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                className="custom-control-input"
                                                                id={`company-${id}`}
                                                                value={id}
                                                                checked={selectedCompanies.includes(id)}
                                                                onChange={() => toggleCompany(id)}
                                                            />
                                                            <label
                                                                className="custom-control-label"
                                                                htmlFor={`company-${id}`}
                                                                style={{ fontSize: '14px' }}
                                                            >
                                                                {company?.company_name}
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            {filteredCompanyList.length === 0 && (
                                                <div className="text-muted small">No companies found</div>
                                            )}
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>
                        )}

                        {
                            isViewOnly ? '' :
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>

                                                <Label for="passwordUpdate">Update Password?</Label>
                                                <CustomInput
                                                    type="switch"
                                                    id="passwordUpdate"
                                                    name="passwordUpdate"
                                                    label={passwordUpdate ? 'Yes' : 'No'}
                                                    checked={passwordUpdate}
                                                    onChange={() => {
                                                        setPasswordUpdate(!passwordUpdate)
                                                    }
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="statusSelect">Status</Label>
                                                <CustomInput
                                                    type="switch"
                                                    id="statusSelect"
                                                    name="status"
                                                    label={status ? 'Active' : 'Inactive'}
                                                    checked={status}
                                                    onChange={handleStatusChange}
                                                    disabled={isViewOnly}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    {
                                        passwordUpdate ?

                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label for="password">New Password</Label>
                                                        <Input
                                                            id="password"
                                                            name="password"
                                                            placeholder="Enter New Password"
                                                            type={isPasswordVisible ? "text" : "password"}
                                                            value={password}
                                                            invalid={formError.password}
                                                            onChange={(e) => {
                                                                setPassword(e.target.value)
                                                                validatePassword(e)
                                                            }}
                                                            disabled={isViewOnly}
                                                        />
                                                        <FormFeedback>Password must be at least 8 characters long, contain at least one number and one special character.</FormFeedback>
                                                    </FormGroup>
                                                </Col>

                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label for="confrimpassword">Confirm New Password</Label>
                                                        <Input
                                                            id="confirmpassword"
                                                            name="confrimpassword"
                                                            placeholder="Confirm New Password"
                                                            type={isConfirmPasswordVisible ? "text" : "password"}
                                                            value={confrimPassword}
                                                            invalid={formError.confirmpassword}
                                                            onChange={(e) => {
                                                                setConfrimPassword(e.target.value)
                                                                validateConfirmPassword(e)
                                                            }}
                                                            disabled={isViewOnly}
                                                        />
                                                        <FormFeedback>Confirm Password must be at least 8 characters long, contain at least one number and one special character.</FormFeedback>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            : ''
                                    }
                                </>
                        }
                        {
                            data?.company_name?.length > 0 && data?.company_name[0] && isViewOnly ?
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="assignedCompany">Company List</Label>
                                            <List>
                                                {data?.company_name.map((item, index) => (
                                                    <li key={index}>
                                                        {item}
                                                    </li>
                                                ))}
                                            </List>
                                        </FormGroup>
                                    </Col>
                                </Row> : ''
                        }

                    </Form>
                </ModalBody>
                <ModalFooter>
                    {
                        isViewOnly ? '' :

                            <Button color="primary" onClick={handleFormSubmit} disabled={loading}>
                                {loading ? <Spinner size="sm" /> : 'Update User'}
                            </Button>
                    }
                    <Button color="secondary" style={isViewOnly ? { outline: 'none', boxShadow: 'none', backgroundColor: '#135674', color: 'white' } : {}} onClick={toggleModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}

export default UpdateViewUser;